/**
 * AtlasDocuments — the "Documents" tab for the Knowledge Atlas.
 *
 * Operator direction 2026-07-06 (Slide 1 Callout D of the
 * DI_KA_Mapping_Functions review):
 *   "We need to be able to see the full list of documents in a
 *    user friendly way. Also make it clear what is 'new' — what
 *    was last loaded or in the last 24 hours by recency."
 *
 * Contract:
 *   • Reads from `GET /api/ka/documents?tenant=X`.
 *   • Most-recent-first sort (server-side).
 *   • Amber "NEW" pill on any document uploaded within 24 hours.
 *   • Search-in-list filter over filename.
 *   • Recency-scope buttons: All · Last 24 h · Since last refresh
 *     ("Since last refresh" respects the same sessionStorage
 *     `ka_new_since_<tenant>` marker used by the Neuropathway view,
 *     so uploads → reindex flow lights up here too).
 */
import React, { useEffect, useMemo, useState } from 'react';
import authedAxios from '../../lib/authedAxios';
import { FileText, Loader2, Download } from 'lucide-react';
import { useDocumentDownload } from '../../hooks/useDocumentDownload';

const API_ROOT = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year:   'numeric',
      month:  'short',
      day:    '2-digit',
      hour:   '2-digit',
      minute: '2-digit',
    });
  } catch (_) { return iso; }
};

const formatSize = (bytes) => {
  if (!bytes) return '—';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const fileTypePill = (t) => {
  const s = (t || '').toLowerCase();
  if (s.includes('pdf'))     return 'PDF';
  if (s.includes('docx'))    return 'DOCX';
  if (s.includes('pptx'))    return 'PPTX';
  if (s.includes('csv'))     return 'CSV';
  if (s.includes('json'))    return 'JSON';
  if (s.includes('markdown') || s.includes('md')) return 'MD';
  if (s.includes('html'))    return 'HTML';
  if (s.includes('text') || s.includes('plain') || s.endsWith('.txt')) return 'TXT';
  return s.toUpperCase().slice(0, 5) || '?';
};

export const AtlasDocuments = ({ tenant, newSince }) => {
  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [filter, setFilter]     = useState('');
  const [scope, setScope]       = useState('all');   // 'all' | '24h' | 'since'
  const { downloadDocument, downloading } = useDocumentDownload();

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!tenant) { if (alive) setDocs([]); return; }
      setLoading(true);
      setError(null);
      try {
        const r = await authedAxios.get(
          `${API_ROOT}/api/ka/documents?tenant=${encodeURIComponent(tenant)}&limit=500`
        );
        if (!alive) return;
        setDocs(r.data?.documents || []);
      } catch (e) {
        if (alive) setError(e.message || 'Failed to load documents');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tenant]);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    let base = docs;
    if (scope === '24h') {
      base = base.filter((d) => d.is_new_24h);
    } else if (scope === 'since' && newSince) {
      const since = new Date(newSince).getTime();
      base = base.filter((d) => {
        if (!d.upload_date) return false;
        try { return new Date(d.upload_date).getTime() >= since; }
        catch { return false; }
      });
    }
    if (!needle) return base;
    // SPD v5.66 · D4 Issue 4 — search across both semantic title and filename
    // so users can find a doc regardless of which label they remember.
    return base.filter((d) => {
      const hay = ((d.semantic_title || '') + ' ' + (d.filename || '')).toLowerCase();
      return hay.includes(needle);
    });
  }, [docs, filter, scope, newSince]);

  if (!tenant) {
    return (
      <div className="mt-8 rounded border border-cyan-400/20 bg-slate-900/40 p-6 text-center text-cyan-300/70">
        Select a Topic to see its Documents.
      </div>
    );
  }

  return (
    <div className="mt-4" data-testid="atlas-documents">
      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative group">
          <input
            type="search"
            data-testid="atlas-documents-search"
            placeholder="Filter by filename…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-64 rounded border border-cyan-400/30 bg-slate-900/60 px-3 py-1.5 text-sm text-cyan-100 placeholder-cyan-300/40 focus:border-cyan-400 focus:outline-none"
          />
        </div>
        <div
          className="flex items-center overflow-hidden rounded-full border border-cyan-400/40 bg-slate-900/60 text-[10px] uppercase tracking-widest"
          data-testid="atlas-documents-scope"
        >
          <button
            type="button"
            data-testid="atlas-documents-scope-all"
            onClick={() => setScope('all')}
            className={
              'px-2.5 py-0.5 transition-colors ' +
              (scope === 'all'
                ? 'bg-cyan-500/25 text-cyan-100'
                : 'text-cyan-300/60 hover:text-cyan-100')
            }
          >
            All
          </button>
          <button
            type="button"
            data-testid="atlas-documents-scope-24h"
            onClick={() => setScope('24h')}
            className={
              'px-2.5 py-0.5 transition-colors ' +
              (scope === '24h'
                ? 'bg-cyan-500/25 text-cyan-100'
                : 'text-cyan-300/60 hover:text-cyan-100')
            }
          >
            Last 24 h
          </button>
          <button
            type="button"
            data-testid="atlas-documents-scope-since"
            onClick={() => setScope('since')}
            disabled={!newSince}
            title={newSince
              ? `Since the last refresh (${formatDate(newSince)})`
              : 'No refresh recorded this session'}
            className={
              'px-2.5 py-0.5 transition-colors ' +
              (scope === 'since'
                ? 'bg-cyan-500/25 text-cyan-100'
                : 'text-cyan-300/60 hover:text-cyan-100 disabled:opacity-40')
            }
          >
            Since last refresh
          </button>
        </div>
        {/* Documents "?" help button + tooltip removed per D9 Cluster 3
            follow-up C ruling (2026-02-08) — dead affordance with no
            other function once the tooltip is stripped. */}
      </div>

      {loading && (
        <div className="flex items-center gap-2 rounded border border-cyan-400/20 bg-slate-900/40 px-3 py-2 text-sm text-cyan-300/80">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading documents…
        </div>
      )}
      {error && (
        <div className="rounded border border-red-400/50 bg-red-500/10 px-3 py-2 text-sm text-red-100/90">
          Failed to load documents: {error}
        </div>
      )}
      {!loading && !error && filtered.length === 0 && (
        <div
          data-testid="atlas-documents-empty"
          className="rounded border border-cyan-400/20 bg-slate-900/40 px-3 py-6 text-center text-sm text-cyan-300/70"
        >
          No documents match. Upload an artefact via the Knowledge Graph icon on
          the Knowledge Controls page and refresh the Atlas to see it here.
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div
          data-testid="atlas-documents-table"
          className="overflow-hidden rounded-lg border border-cyan-400/20 bg-slate-950/70"
        >
          <div className="grid grid-cols-[minmax(0,1fr)_80px_180px_90px_100px_60px] gap-3 border-b border-cyan-400/20 bg-slate-900/70 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-cyan-300/70">
            <span>Filename</span>
            <span>Type</span>
            <span>Uploaded</span>
            <span className="text-right">Size</span>
            <span className="text-right">Concepts</span>
            <span className="text-right">Get</span>
          </div>
          <div className="max-h-[520px] overflow-auto">
            {filtered.map((d) => {
              const isDownloading = !!downloading[d.id];
              return (
              <div
                key={d.id}
                data-testid={`atlas-documents-row-${d.id}`}
                className="grid grid-cols-[minmax(0,1fr)_80px_180px_90px_100px_60px] items-center gap-3 border-b border-cyan-400/10 px-4 py-2 text-sm text-cyan-100/90 hover:bg-slate-900/70"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-cyan-400/70" />
                  <span className="truncate" title={d.filename}>
                    {d.semantic_title || d.filename || d.id}
                  </span>
                  {d.is_new_24h && (
                    <span
                      data-testid="atlas-documents-new-badge"
                      className="ml-1 shrink-0 rounded-full border border-amber-400/60 bg-amber-500/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest text-amber-200"
                    >
                      New
                    </span>
                  )}
                </div>
                <span className="rounded bg-slate-800/70 px-1.5 py-0.5 text-center font-mono text-[10px] text-cyan-300/80">
                  {fileTypePill(d.file_type)}
                </span>
                <span className="font-mono text-[11px] text-cyan-200/80">
                  {formatDate(d.upload_date)}
                </span>
                <span className="text-right font-mono text-[11px] text-cyan-200/70">
                  {formatSize(d.size_bytes)}
                </span>
                <span className="text-right font-mono text-[11px] text-cyan-200/70">
                  {d.entities_extracted ?? 0}
                </span>
                {/* Download button — Q5 · Public-App Un-hide (2026-02-09).
                    Auth'd via JWT via useDocumentDownload hook. Investors
                    can pull the original artefact for any listed doc. */}
                <div className="text-right">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); downloadDocument(d.id, d.filename); }}
                    disabled={isDownloading}
                    data-testid={`atlas-documents-download-${d.id}`}
                    className="inline-flex h-6 w-6 items-center justify-center rounded border border-cyan-400/40 bg-slate-900/60 text-cyan-300/80 transition-colors hover:border-cyan-300 hover:text-cyan-100 disabled:opacity-50"
                    title={isDownloading ? 'Downloading…' : `Download ${d.filename || d.id}`}
                  >
                    <Download className="h-3 w-3" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
          <div className="border-t border-cyan-400/20 bg-slate-900/50 px-4 py-1.5 text-right font-mono text-[10px] uppercase tracking-widest text-cyan-300/60">
            {filtered.length} of {docs.length} documents
          </div>
        </div>
      )}
    </div>
  );
};

export default AtlasDocuments;
